import RenterDriverPanel from "../renter-drivers/RenterDriverPanel";
import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "../../lib/api";
import {
  X, Camera, Trash2, CheckCircle2, AlertTriangle, KeyRound, Lock,
  Gauge, ShieldAlert, ExternalLink, QrCode, ScanLine, Eye, Flag,
} from "lucide-react";
import { toast } from "sonner";

const FUEL_STEPS = [
  { v: 0, l: "Empty" }, { v: 25, l: "1/4" }, { v: 50, l: "1/2" },
  { v: 75, l: "3/4" }, { v: 100, l: "Full" },
];

const PHOTO_TAGS = [
  "front", "rear", "left", "right", "interior", "odometer", "fuel_display", "existing_damage", "other",
];

const SKIP_REASONS = [
  { v: "unreadable", l: "Odometer unreadable" },
  { v: "not_working", l: "Odometer not working" },
  { v: "does_not_track_mileage", l: "Vehicle doesn't track mileage" },
  { v: "other", l: "Other" },
];

const STEPS = ["Verify Vehicle", "Odometer & Fuel", "Existing Damage", "Photos", "Keys & Items", "Complete"];

/**
 * Mobile-first pickup handover wizard. Owns the "Confirmed -> Ongoing"
 * checkpoint end to end: starting/resuming the handover, capturing
 * odometer/fuel/checklist/photos on the linked pickup inspection, the
 * existing-damage review step, keys/items, and final completion.
 *
 * Every step saves as it goes (not held until a final submit), if the
 * connection drops mid-wizard, whatever was already saved survives a reload.
 *
 * Props: bookingId, open, onClose, onDone
 */
export default function PickupHandoverWizard({ bookingId, open, onClose, onDone, preloaded = null }) {
  const [handover, setHandover] = useState(null);
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [startOverride, setStartOverride] = useState(null); // { vehicleStatus, note } | null
  const [skipReason, setSkipReason] = useState("");
  const [skipNote, setSkipNote] = useState("");
  const fileRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setStep(0);
    setStartOverride(null);
    if (preloaded) {
      setHandover(structuredClone(preloaded));
      setPhotos(structuredClone(preloaded.photos || []));
      setLoading(false);
    } else {
      start();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, bookingId, preloaded]);

  const start = async (overrideNote, dispatchAcknowledgement) => {
    setLoading(true);
    try {
      const body = {};
      if (overrideNote) body.override_note = overrideNote;
      if (dispatchAcknowledgement) body.dispatch_acknowledgement = dispatchAcknowledgement;
      const { data } = await api.post(`/bookings/${bookingId}/pickup-handover/start`, body);
      setHandover(data);
      setStartOverride(null);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail?.code === "vehicle_not_ready" && !overrideNote) {
        setStartOverride({ vehicleStatus: detail.vehicle_status, note: "" });
      } else if (detail?.code === "dispatch_acknowledgement_required" && !dispatchAcknowledgement) {
        // Task 5: with-driver REVIEW items need an authorised acknowledgement.
        // BLOCKED dispatch never offers this form.
        const reasons = Object.values(detail.gates || {}).flatMap((g) => g.reasons || [])
          .filter((r) => r.severity === "REVIEW");
        setStartOverride({ kind: "dispatch", codes: detail.review_codes || [], reasons, note: "" });
      } else {
        toast.error(formatApiError(e));
        onClose?.();
      }
    } finally {
      setLoading(false);
    }
  };

  // Phase D: physical vehicle identity verification. Skipped entirely in
  // demo/preloaded mode (sandboxed scripted data, no real backend calls) -
  // that step just shows a friendly not-applicable note and never blocks.
  const [safety, setSafety] = useState(null);
  const [expectedVehicle, setExpectedVehicle] = useState(null);
  const [verifyMode, setVerifyMode] = useState("qr");
  const [qrInput, setQrInput] = useState("");
  const [plateInput, setPlateInput] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [verifyResult, setVerifyResult] = useState(null);

  useEffect(() => {
    if (preloaded || !handover?.id) return;
    let cancelled = false;
    (async () => {
      try {
        const [{ data: s }, { data: card }] = await Promise.all([
          api.get("/enhanced-safety/settings"),
          api.get(`/pickup-handovers/${handover.id}/expected-vehicle`),
        ]);
        if (!cancelled) { setSafety(s); setExpectedVehicle(card); }
      } catch (_) { /* tolerate: the step still renders without the card/requirement */ }
    })();
    return () => { cancelled = true; };
  }, [handover?.id, preloaded]);

  const verifyRequired = !preloaded && safety
    && (safety.state === "active" || safety.state === "deactivation_requested");
  const alreadyVerified = handover?.vehicle_verification?.status === "verified";

  const runVerify = async (method, extra = {}) => {
    if (!handover || preloaded) return;
    setVerifying(true);
    setVerifyResult(null);
    try {
      const { data } = await api.post(`/pickup-handovers/${handover.id}/verify-vehicle`, { method, ...extra });
      setVerifyResult(data);
      if (data.result === "SUCCESS") {
        setHandover((prev) => prev && ({ ...prev, vehicle_verification: data.vehicle_verification }));
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setVerifying(false);
    }
  };

  const reportAttemptedHandover = async () => {
    if (!handover || !verifyResult) return;
    try {
      await api.post(`/bookings/${bookingId}/report-attempted-handover`, {
        category: verifyResult.category || "OTHER_VERIFICATION_FAILURE",
      });
      toast.success("Reported: the business can review this in the vehicle's activity history.");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const insp = handover?.inspection;
  const completed = handover?.status === "completed";

  const saveInspection = async (patch) => {
    if (!insp || completed) return;
    if (preloaded) {
      setHandover((prev) => prev && ({ ...prev, inspection: { ...prev.inspection, ...patch } }));
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.put(`/inspections/${insp.id}`, patch);
      setHandover((prev) => prev && ({ ...prev, inspection: data }));
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const saveHandover = async (patch) => {
    if (!handover || completed) return;
    if (preloaded) {
      setHandover((prev) => prev && ({ ...prev, ...patch }));
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.put(`/pickup-handovers/${handover.id}`, patch);
      setHandover(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const onPhotoPicked = async (e) => {
    const file = e.target.files?.[0];
    const tag = fileRef.current?.dataset?.tag || "";
    e.target.value = "";
    if (!file || !insp) return;
    if (file.size > 4 * 1024 * 1024) {
      toast.error("Photo too large: max 4MB.");
      return;
    }
    setSaving(true);
    try {
      const dataUrl = await new Promise((resolve, reject) => {
        const fr = new FileReader();
        fr.onload = () => resolve(fr.result);
        fr.onerror = reject;
        fr.readAsDataURL(file);
      });
      if (preloaded) {
        const demoPhoto = { id: `demo-photo-${Date.now()}`, data_url: dataUrl, name: file.name, tag };
        setPhotos((prev) => [...prev, demoPhoto]);
        setHandover((prev) => prev && ({ ...prev, inspection: { ...prev.inspection, photo_count: (prev.inspection?.photo_count || 0) + 1 } }));
        return;
      }
      const base64 = dataUrl.split(",")[1];
      await api.post(`/inspections/${insp.id}/photos`,
        { name: file.name, content_type: file.type || "image/jpeg", data_base64: base64, tag });
      const { data } = await api.get(`/bookings/${bookingId}/pickup-handover`);
      setHandover(data);
      toast.success("Photo added");
    } catch (e) {
      // A single failed upload doesn't wipe out anything already saved -
      // the handover/inspection state is untouched, staff can just retry.
      toast.error(formatApiError(e) || "Upload failed: try again.");
    } finally {
      setSaving(false);
    }
  };

  const [photos, setPhotos] = useState([]);
  useEffect(() => {
    if (preloaded) return;
    if (!insp?.id || !open) { setPhotos([]); return; }
    (async () => {
      try {
        const { data } = await api.get(`/inspections/${insp.id}`);
        setPhotos(data.photos || []);
      } catch (_) { /* ignore */ }
    })();
  }, [insp?.id, insp?.photo_count, open, preloaded]);

  const deletePhoto = async (pid) => {
    if (preloaded) {
      setPhotos((prev) => prev.filter((p) => p.id !== pid));
      setHandover((prev) => prev && ({ ...prev, inspection: { ...prev.inspection, photo_count: Math.max(0, (prev.inspection?.photo_count || 1) - 1) } }));
      return;
    }
    try {
      await api.delete(`/inspections/${insp.id}/photos/${pid}`);
      setPhotos((prev) => prev.filter((p) => p.id !== pid));
      setHandover((prev) => prev && ({
        ...prev,
        inspection: { ...prev.inspection, photo_count: Math.max(0, (prev.inspection?.photo_count || 1) - 1) },
      }));
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const toggleKeyItem = (idx) => {
    // Functional update, not a read of the `handover` closure: several
    // checkboxes can be tapped in quick succession (plausible on a phone),
    // and reading the stale outer `handover` for each tap would make later
    // saves silently overwrite earlier ones. Updating via the previous-state
    // callback means each tap builds on whatever the last tap already
    // applied, both locally and in the persisted payload.
    setHandover((prev) => {
      if (!prev) return prev;
      const items = (prev.keys_items_released || []).map((k, i) =>
        i === idx ? { ...k, released: !k.released } : k);
      if (preloaded) return { ...prev, keys_items_released: items };
      api.put(`/pickup-handovers/${prev.id}`, { keys_items_released: items })
        .then(({ data }) => setHandover((cur) => (cur && cur.id === data.id ? data : cur)))
        .catch((e) => toast.error(formatApiError(e)));
      return { ...prev, keys_items_released: items };
    });
  };

  const complete = async () => {
    if (!insp?.odometer_km && !skipReason) {
      toast.error("Record the odometer, or choose why it wasn't recorded.");
      return;
    }
    if (skipReason === "other" && !skipNote.trim()) {
      toast.error("Add a short note for 'Other'.");
      return;
    }
    setSaving(true);
    try {
      if (preloaded) {
        const completedHandover = { ...handover, status: "completed", completed_at: new Date().toISOString() };
        setHandover(completedHandover);
        onDone?.(completedHandover);
        onClose?.();
        return;
      }
      const { data } = await api.post(`/pickup-handovers/${handover.id}/complete`, {
        odometer_skip_reason: insp?.odometer_km ? null : skipReason,
        odometer_skip_note: insp?.odometer_km ? null : (skipNote.trim() || null),
      });
      setHandover(data);
      toast.success("Pickup handover completed: booking is now Ongoing.");
      onDone?.();
      onClose?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <div
      data-testid="pickup-handover-wizard"
      className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-end sm:items-center justify-center p-2 sm:p-6"
    >
      <div className="w-full max-w-2xl bg-white rounded-2xl shadow-xl max-h-[95vh] overflow-hidden flex flex-col">
        <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
          <h2 className="font-display text-xl font-semibold inline-flex items-center gap-2">
            <KeyRound className="w-5 h-5 text-[#1B4332]" /> Pickup Handover
            {completed && (
              <span className="ml-1 inline-flex items-center gap-1 text-[11px] uppercase tracking-wider bg-emerald-50 text-emerald-700 border border-emerald-200 px-2 py-0.5 rounded-full">
                <Lock className="w-3 h-3" /> Completed
              </span>
            )}
          </h2>
          <button onClick={onClose} className="p-2 rounded-md hover:bg-slate-100" data-testid="handover-close">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Progress */}
        {!startOverride && (
          <div className="px-5 pt-3 flex items-center gap-1.5 flex-wrap" data-testid="handover-progress">
            {STEPS.map((label, i) => (
              <button key={label} type="button" onClick={() => setStep(i)}
                      className={`text-[11px] px-2 py-1 rounded-full border whitespace-nowrap ${
                        i === step ? "bg-[#1B4332] text-white border-[#1B4332]"
                          : i < step ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                          : "bg-white text-slate-500 border-slate-200"
                      }`}>
                {i + 1}. {label}
              </button>
            ))}
          </div>
        )}

        <div className="flex-1 overflow-y-auto p-5 space-y-5">
          {loading && <div className="text-sm text-slate-500">Loading…</div>}

          {startOverride?.kind === "dispatch" && (
            <div className="rounded-lg border border-amber-300 bg-amber-50 p-4 space-y-3" data-testid="handover-dispatch-ack">
              <div className="font-semibold text-amber-900">This with-driver trip has items to review before it starts</div>
              <ul className="text-sm text-amber-900 list-disc pl-5">
                {startOverride.reasons.map((r) => <li key={r.code}>{r.message}</li>)}
              </ul>
              <textarea rows={2} value={startOverride.note}
                        onChange={(e) => setStartOverride((p) => p && ({ ...p, note: e.target.value }))}
                        placeholder="Why the trip can go ahead (10 characters or more)"
                        data-testid="handover-dispatch-ack-note"
                        className="w-full text-sm rounded-md border border-slate-300 p-2" />
              <div className="flex justify-end gap-2">
                <button onClick={onClose} className="px-3 py-1.5 text-sm rounded-md hover:bg-amber-100">Cancel</button>
                <button onClick={() => start(null, { codes: startOverride.codes, reason: startOverride.note.trim() })}
                        disabled={startOverride.note.trim().length < 10}
                        className="px-3 py-1.5 text-sm rounded-md bg-amber-700 text-white disabled:opacity-50">
                  Acknowledge and start</button>
              </div>
            </div>
          )}
          {startOverride && startOverride.kind !== "dispatch" && (
            <div className="rounded-lg border border-amber-300 bg-amber-50 p-4 space-y-3" data-testid="handover-start-override">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-amber-700 mt-0.5" />
                <div>
                  <div className="font-semibold text-amber-900">
                    This vehicle is currently &ldquo;{startOverride.vehicleStatus}&rdquo;
                  </div>
                  <p className="text-sm text-amber-900/80 mt-0.5">
                    Describe why it&apos;s safe to hand over anyway to start the pickup.
                  </p>
                </div>
              </div>
              <textarea rows={2} value={startOverride.note}
                        onChange={(e) => setStartOverride((p) => p && ({ ...p, note: e.target.value }))}
                        placeholder="e.g. Inspected in person: no issues found."
                        data-testid="handover-start-override-note"
                        className="w-full text-sm rounded-md border border-slate-300 p-2" />
              <div className="flex justify-end gap-2">
                <button onClick={onClose} className="px-3 py-1.5 text-sm rounded-md hover:bg-amber-100">Cancel</button>
                <button onClick={() => startOverride.note.trim() && start(startOverride.note.trim())}
                        disabled={!startOverride.note.trim()}
                        data-testid="handover-start-override-confirm"
                        className="px-3 py-1.5 text-sm rounded-md bg-amber-600 text-white hover:opacity-90 disabled:opacity-50">
                  Start pickup anyway
                </button>
              </div>
            </div>
          )}

          {handover && !startOverride && step === 0 && (
            <div className="space-y-4" data-testid="handover-verify-step">
              {!preloaded && (
                // Task 6: who may drive, and the pickup document checks, before release.
                <RenterDriverPanel bookingId={bookingId} data={{ rental_service_type: "self_drive" }} />
              )}
              {preloaded ? (
                <p className="text-sm text-slate-500 italic">Vehicle verification isn&apos;t part of this demo.</p>
              ) : (
                <>
                  {expectedVehicle && (
                    <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 flex items-start gap-3" data-testid="handover-expected-vehicle">
                      {expectedVehicle.photo_data_url && (
                        <img src={expectedVehicle.photo_data_url} alt="" className="w-16 h-16 rounded-lg object-cover border border-slate-200" />
                      )}
                      <div>
                        <div className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Expected vehicle</div>
                        <div className="font-medium text-slate-800">
                          {expectedVehicle.make} {expectedVehicle.model}
                          {expectedVehicle.color ? ` · ${expectedVehicle.color}` : ""}
                        </div>
                        {expectedVehicle.plate_number && (
                          <div className="text-sm text-slate-600 font-mono">{expectedVehicle.plate_number}</div>
                        )}
                      </div>
                    </div>
                  )}

                  {alreadyVerified ? (
                    <div className="rounded-lg border border-emerald-300 bg-emerald-50 p-3 flex items-start gap-2" data-testid="handover-verify-success">
                      <CheckCircle2 className="w-4 h-4 text-emerald-700 mt-0.5" />
                      <div className="text-sm text-emerald-900">
                        Vehicle verified for this ConfirmPH handover
                        {handover.vehicle_verification?.method ? ` (${handover.vehicle_verification.method})` : ""}.
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="flex gap-1.5 flex-wrap">
                        {[["qr", "Scan / Enter Code", QrCode], ["plate", "Enter Plate", ScanLine], ["visual", "Visual Confirmation", Eye]].map(([m, l, Icon]) => (
                          <button key={m} type="button" onClick={() => { setVerifyMode(m); setVerifyResult(null); }}
                                  data-testid={`handover-verify-mode-${m}`}
                                  className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border text-xs ${
                                    verifyMode === m ? "bg-[#1B4332] text-white border-[#1B4332]" : "bg-white border-slate-300 hover:bg-slate-50"
                                  }`}>
                            <Icon className="w-3.5 h-3.5" /> {l}
                          </button>
                        ))}
                      </div>

                      {verifyMode === "qr" && (
                        <div className="flex gap-2">
                          <input value={qrInput} onChange={(e) => setQrInput(e.target.value)}
                                 placeholder="Scan with a camera app, or paste/type the code"
                                 data-testid="handover-verify-qr-input"
                                 className="flex-1 text-sm rounded-md border border-slate-300 px-3 py-2" />
                          <button onClick={() => runVerify("qr", { qr_token: qrInput })}
                                  disabled={verifying || !qrInput.trim()} data-testid="handover-verify-qr-submit"
                                  className="px-3 py-2 rounded-md bg-[#1B4332] text-white text-sm disabled:opacity-50">
                            Verify
                          </button>
                        </div>
                      )}
                      {verifyMode === "plate" && (
                        <div className="flex gap-2">
                          <input value={plateInput} onChange={(e) => setPlateInput(e.target.value)}
                                 placeholder="Plate number on the vehicle"
                                 data-testid="handover-verify-plate-input"
                                 className="flex-1 text-sm rounded-md border border-slate-300 px-3 py-2" />
                          <button onClick={() => runVerify("plate", { plate: plateInput })}
                                  disabled={verifying || !plateInput.trim()} data-testid="handover-verify-plate-submit"
                                  className="px-3 py-2 rounded-md bg-[#1B4332] text-white text-sm disabled:opacity-50">
                            Verify
                          </button>
                        </div>
                      )}
                      {verifyMode === "visual" && (
                        <button onClick={() => runVerify("visual")} disabled={verifying}
                                data-testid="handover-verify-visual-submit"
                                className="px-3 py-2 rounded-md bg-[#1B4332] text-white text-sm disabled:opacity-50">
                          I&apos;ve visually confirmed this is the correct vehicle
                        </button>
                      )}
                    </>
                  )}

                  {verifyResult && verifyResult.result !== "SUCCESS" && (
                    <div className="rounded-lg border border-red-300 bg-red-50 p-3 space-y-2" data-testid="handover-verify-blocked">
                      <div className="flex items-start gap-2">
                        <ShieldAlert className="w-4 h-4 text-red-700 mt-0.5" />
                        <div className="text-sm text-red-900">{verifyResult.message}</div>
                      </div>
                      {(verifyResult.expected || verifyResult.presented) && (
                        <div className="text-xs text-red-900/80 grid grid-cols-2 gap-2">
                          <div>
                            <div className="font-semibold uppercase tracking-wide">Expected</div>
                            {verifyResult.expected
                              ? `${verifyResult.expected.make || ""} ${verifyResult.expected.model || ""} · ${verifyResult.expected.plate_number || "?"}`
                              : "—"}
                          </div>
                          <div>
                            <div className="font-semibold uppercase tracking-wide">Presented</div>
                            {verifyResult.presented
                              ? `${verifyResult.presented.make || ""} ${verifyResult.presented.model || ""} · ${verifyResult.presented.plate_number || "?"}`
                              : "—"}
                          </div>
                        </div>
                      )}
                      <button onClick={reportAttemptedHandover} data-testid="handover-report-attempted"
                              className="inline-flex items-center gap-1.5 text-xs text-red-800 hover:underline">
                        <Flag className="w-3 h-3" /> Report attempted handover
                      </button>
                    </div>
                  )}

                  {verifyRequired && !alreadyVerified && (
                    <p className="text-xs text-amber-700">
                      Enhanced Safety Enforcement is active for this business: verify the vehicle before continuing.
                    </p>
                  )}
                </>
              )}
            </div>
          )}

          {handover && !startOverride && step === 1 && (
            <div className="space-y-4">
              <label className="block">
                <span className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500 inline-flex items-center gap-1">
                  <Gauge className="w-3.5 h-3.5" /> Odometer (km)
                </span>
                <input type="number" inputMode="numeric" disabled={completed}
                       value={insp?.odometer_km ?? ""} data-testid="handover-odometer"
                       onChange={(e) => saveInspection({ odometer_km: e.target.value === "" ? null : Math.max(0, Number(e.target.value)) })}
                       className="mt-1 w-full rounded-md border border-slate-300 px-3 py-3 text-lg disabled:bg-slate-100" />
                <p className="text-xs text-slate-500 mt-1">
                  Strongly recommended: mileage history and maintenance alerts depend on it. If you can&apos;t record it now, you can explain why on the last step.
                </p>
              </label>
              <div>
                <span className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Fuel / charge level</span>
                <div className="mt-1 grid grid-cols-5 gap-1.5">
                  {FUEL_STEPS.map((f) => (
                    <button key={f.v} type="button" disabled={completed}
                            data-testid={`handover-fuel-${f.v}`}
                            onClick={() => saveInspection({ fuel_level: f.v })}
                            className={`py-3 rounded-lg border text-sm font-medium ${
                              insp?.fuel_level === f.v ? "bg-[#1B4332] text-white border-[#1B4332]"
                                : "bg-white border-slate-200 hover:bg-slate-50"
                            }`}>
                      {f.l}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {handover && !startOverride && step === 2 && (
            <div className="space-y-3">
              <div className="flex items-start gap-2">
                <ShieldAlert className="w-4 h-4 text-slate-500 mt-0.5" />
                <p className="text-sm text-slate-600">
                  Review the vehicle&apos;s recorded existing damage before release. This is staff review of prior
                  condition: separate from the customer&apos;s acknowledgement on the last step.
                </p>
              </div>
              {(handover.existing_damage || []).length === 0 ? (
                <div className="text-sm text-slate-500 italic rounded-lg border border-dashed border-slate-300 p-3">
                  No existing damage on record for this vehicle.
                </div>
              ) : (
                <div className="rounded-lg border border-slate-200 divide-y divide-slate-100">
                  {handover.existing_damage.map((d) => (
                    <div key={d.id} className="p-3 text-sm flex items-center justify-between gap-2" data-testid={`handover-damage-${d.id}`}>
                      <div>
                        <span className="font-medium capitalize">{(d.tag_code || "").replace(/_/g, " ")}</span>
                        <span className="text-slate-500"> · {d.view} · {d.severity}</span>
                        {d.notes && <div className="text-xs text-slate-500 mt-0.5">{d.notes}</div>}
                      </div>
                    </div>
                  ))}
                </div>
              )}
              <Link to={preloaded ? "#" : `/app/vehicles/${handover.item_id}/damages`} target={preloaded ? undefined : "_blank"}
                    onClick={preloaded ? (e) => e.preventDefault() : undefined}
                    className="inline-flex items-center gap-1 text-xs text-[#1B4332] hover:underline">
                View full damage map <ExternalLink className="w-3 h-3" />
              </Link>
              <label className="flex items-center gap-2 text-sm rounded-lg bg-slate-50 border border-slate-200 p-3">
                <input type="checkbox" disabled={completed}
                       checked={!!handover.existing_damage_reviewed}
                       data-testid="handover-damage-reviewed"
                       onChange={(e) => saveHandover({ existing_damage_reviewed: e.target.checked })} />
                <span>Existing damage reviewed before release.</span>
              </label>
            </div>
          )}

          {handover && !startOverride && step === 3 && (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="font-display text-base font-semibold">Photos ({photos.length})</h3>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {PHOTO_TAGS.map((tag) => (
                  <button key={tag} type="button" disabled={completed}
                          data-testid={`handover-photo-tag-${tag}`}
                          onClick={() => { fileRef.current.dataset.tag = tag; fileRef.current?.click(); }}
                          className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-slate-300 bg-white hover:bg-slate-50 text-xs capitalize">
                    <Camera className="w-3.5 h-3.5" /> {tag.replace(/_/g, " ")}
                  </button>
                ))}
              </div>
              <input type="file" accept="image/*" capture="environment" ref={fileRef}
                     onChange={onPhotoPicked} hidden data-testid="handover-photo-input" />
              {photos.length > 0 && (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {photos.map((p) => (
                    <div key={p.id} className="relative group rounded-lg overflow-hidden border border-slate-200 bg-slate-50" data-testid={`handover-photo-${p.id}`}>
                      <img src={p.data_url} alt={p.tag || p.name} className="w-full h-28 object-cover" />
                      {p.tag && <div className="text-[10px] px-1.5 py-0.5 bg-black/50 text-white absolute top-1 left-1 rounded capitalize">{p.tag.replace(/_/g, " ")}</div>}
                      {!completed && (
                        <button onClick={() => deletePhoto(p.id)} data-testid={`handover-photo-delete-${p.id}`}
                                className="absolute top-1 right-1 p-1 rounded-md bg-white/90 hover:bg-white text-red-600 opacity-0 group-hover:opacity-100 transition">
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {handover && !startOverride && step === 4 && (
            <div className="space-y-2">
              <p className="text-sm text-slate-600 mb-1">Confirm what was released with the vehicle.</p>
              {(handover.keys_items_released || []).map((k, idx) => (
                <label key={k.item} className="flex items-center gap-3 rounded-lg border border-slate-200 p-3 text-sm"
                       data-testid={`handover-item-${idx}`}>
                  <input type="checkbox" disabled={completed} checked={!!k.released}
                         onChange={() => toggleKeyItem(idx)} className="w-5 h-5" />
                  <span className="font-medium">{k.item}</span>
                </label>
              ))}
            </div>
          )}

          {handover && !startOverride && step === 5 && (
            <div className="space-y-4">
              <div className="rounded-lg bg-slate-50 border border-slate-200 p-3">
                <h3 className="font-display text-base font-semibold mb-2">Customer acknowledgement</h3>
                <p className="text-xs text-slate-600 mb-2">
                  Type the customer&apos;s full name: they acknowledge they reviewed the vehicle condition. This is a
                  real customer acceptance, separate from the staff damage review two steps back.
                </p>
                <input type="text" disabled={completed} value={insp?.customer_signature_name || ""}
                       onChange={(e) => saveInspection({ customer_signature_name: e.target.value })}
                       placeholder="Customer's typed name" data-testid="handover-signature"
                       className="w-full text-sm rounded-md border border-slate-300 px-3 py-2 disabled:bg-slate-100" />
                <label className="flex items-center gap-2 text-sm mt-2">
                  <input type="checkbox" disabled={completed} checked={!!insp?.customer_acknowledged}
                         onChange={(e) => saveInspection({ customer_acknowledged: e.target.checked })}
                         data-testid="handover-ack" />
                  <span>Customer has reviewed the vehicle and acknowledges its condition.</span>
                </label>
              </div>

              {!insp?.odometer_km && !completed && (
                <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 space-y-2" data-testid="handover-odometer-skip">
                  <div className="font-medium text-amber-900 text-sm">Odometer wasn&apos;t recorded</div>
                  <select value={skipReason} onChange={(e) => setSkipReason(e.target.value)}
                          data-testid="handover-skip-reason"
                          className="w-full text-sm rounded-md border border-slate-300 px-2 py-2">
                    <option value="">Why not?</option>
                    {SKIP_REASONS.map((r) => <option key={r.v} value={r.v}>{r.l}</option>)}
                  </select>
                  {skipReason === "other" && (
                    <input type="text" value={skipNote} onChange={(e) => setSkipNote(e.target.value)}
                           placeholder="Briefly explain" data-testid="handover-skip-note"
                           className="w-full text-sm rounded-md border border-slate-300 px-2 py-2" />
                  )}
                </div>
              )}

              <label className="block">
                <span className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Internal notes</span>
                <textarea rows={2} disabled={completed} value={handover.internal_notes || ""}
                          onChange={(e) => saveHandover({ internal_notes: e.target.value })}
                          data-testid="handover-internal-notes"
                          className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm disabled:bg-slate-100" />
              </label>
            </div>
          )}
        </div>

        <div className="px-5 py-4 border-t border-slate-200 flex flex-col items-end gap-1.5">
          <div className="w-full flex justify-between gap-2 flex-wrap">
          {handover && !startOverride && !completed ? (
            <>
              <button onClick={() => setStep((s) => Math.max(0, s - 1))} disabled={step === 0}
                      className="px-4 py-2 rounded-lg border border-slate-300 bg-white hover:bg-slate-50 text-sm disabled:opacity-40">
                Back
              </button>
              {step < STEPS.length - 1 ? (
                <button onClick={() => setStep((s) => Math.min(STEPS.length - 1, s + 1))}
                        data-testid="handover-next"
                        disabled={saving || (step === 0 && verifyRequired && !alreadyVerified)}
                        className="px-4 py-2 rounded-lg bg-[#1B4332] text-white hover:opacity-90 text-sm disabled:opacity-50">
                  Next
                </button>
              ) : (
                <button onClick={complete} disabled={saving} data-testid="handover-complete-btn"
                        className="px-4 py-2 rounded-lg bg-[#1B4332] text-white hover:opacity-90 text-sm inline-flex items-center gap-1 disabled:opacity-50">
                  <CheckCircle2 className="w-4 h-4" /> Complete Pickup
                </button>
              )}
            </>
          ) : completed ? (
            <button onClick={onClose} data-testid="handover-done" className="ml-auto px-4 py-2 rounded-lg bg-[#1B4332] text-white text-sm">
              Done
            </button>
          ) : null}
          </div>
          {step === 0 && verifyRequired && !alreadyVerified && !completed && (
            <p className="text-xs text-amber-700" data-testid="handover-verify-required-hint">
              Verify the vehicle above to continue.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
